import {AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Optional, Output, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatCheckbox, MatCheckboxModule} from '@angular/material/checkbox';
import {MatTooltipModule} from '@angular/material/tooltip';

/**
 * One tickbox of the KYB people table, with the affordance for ticks that can never be chosen:
 * a locked tick stays a tick, a locked untick becomes a grey cross, and either way the hover
 * explains why. A merely unselected tick that is disabled for now (view-only) stays a checkbox.
 *
 * The KYC/AML tab turns the cross off so an ineligible signer stays a tickable checkbox; unmet
 * rules are shown as messages under the table instead.
 *
 * The tooltip sits on a wrapper rather than on the checkbox because a disabled element emits no
 * mouse events, so matTooltip on it never fires.
 */
@Component({
  selector: 'kyb-lock-tick',
  templateUrl: './kyb-lock-tick.component.html',
  styleUrls: ['./kyb-lock-tick.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatCheckboxModule, MatTooltipModule],
})
export class KybLockTickComponent implements AfterViewChecked {

  @Input({required: true}) checked = false;
  // Never selectable: still disabled in edit mode. Unchecked locked ticks render as a cross when
  // showLockedCross is on (the wizard).
  @Input({required: true}) locked = false;
  // Currently not interactive (view-only). Unchecked ticks stay a checkbox, not a cross.
  @Input({required: false}) readonly = false;
  // Why the tick cannot be changed. Only read while locked.
  @Input({required: false}) tooltip = '';
  // The wizard uses a grey cross for a locked untick; the KYC/AML tab keeps a checkbox instead.
  @Input({required: false}) showLockedCross = true;
  @Output() checkedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(MatCheckbox) private checkbox?: MatCheckbox;

  constructor(@Optional() private cdr?: ChangeDetectorRef) {}

  get displaysCross(): boolean {
    return this.showLockedCross && this.locked && !this.checked;
  }

  // A held-back tick (details dialog cancelled) clears `checked` without a user click, so the
  // checkbox's internal state must be pushed back or the box keeps showing the tick. Done after the
  // view renders because the @if branch can recreate the checkbox.
  ngAfterViewChecked(): void {
    if (this.checkbox && this.checkbox.checked !== this.checked) {
      this.checkbox.checked = this.checked;
    }
  }

  onCheckedChange(checked: boolean) {
    if (this.locked || this.readonly) {
      return;
    }
    this.checkedChange.emit(checked);
  }
}
